import { useEffect, useState } from "react";
import {
  ArrowRightLeft,
  Layers,
  PenLine,
  Shield,
  Sparkles,
  Users,
} from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { apiFetch, ApiError } from "@/lib/api-client";

interface CanvasRendererProps {
  webinarId: string;
  mode: "presenter" | "viewer";
  variant?: "panel" | "overlay";
}

type CanvasTool = "sticky_note" | "text_block" | "poll_card" | "word_cloud" | "emoji_zone";

interface CanvasFogZone {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  isRevealed: boolean;
  label?: string;
}

interface CanvasBoardElement {
  id: string;
  boardId: string;
  elementType: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zIndex: number;
  content: Record<string, unknown>;
  createdBy: string;
  isPresenterOwned: boolean;
  createdAt: string;
  summary: string;
  interactionCount: number;
}

interface CanvasBoardState {
  id: string;
  webinarId: string;
  title: string;
  audience: "presenter" | "shared";
  isPresenterOnly: boolean;
  status: string;
  fogZones: CanvasFogZone[];
  width: number;
  height: number;
  backgroundColor: string;
  sortOrder: number;
  elementCount: number;
  maxElements: number;
  createdAt: string;
  elements: CanvasBoardElement[];
}

interface CanvasTimelineEntry {
  id: string;
  boardId: string;
  boardTitle: string;
  boardAudience: "presenter" | "shared";
  elementType: string;
  summary: string;
  createdBy: string;
  createdAt: string;
  timestampSeconds: number | null;
  isPresenterOwned: boolean;
  interactionCount: number;
}

interface CanvasArtifactsResponse {
  boards: CanvasBoardState[];
  timeline: CanvasTimelineEntry[];
  summary: {
    totalArtifacts: number;
    attendeeArtifacts: number;
    presenterArtifacts: number;
  };
}

const TOOL_CONFIG: Array<{ key: CanvasTool; label: string }> = [
  { key: "sticky_note", label: "Sticky note" },
  { key: "text_block", label: "Text block" },
  { key: "poll_card", label: "Vote card" },
  { key: "word_cloud", label: "Word cloud" },
  { key: "emoji_zone", label: "Emoji zone" },
];

export function CanvasRenderer({ webinarId, mode, variant = "panel" }: CanvasRendererProps) {
  const [data, setData] = useState<CanvasArtifactsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTool, setActiveTool] = useState<CanvasTool>("sticky_note");
  const [textDraft, setTextDraft] = useState("Biggest blocker?");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusNotice, setFocusNotice] = useState<string | null>(null);

  async function loadCanvasState() {
    setError(null);
    try {
      const response = await apiFetch<{ data: CanvasArtifactsResponse }>(`/v1/webinars/${webinarId}/canvas/artifacts`);
      setData(response.data);
    } catch (loadError) {
      const message = loadError instanceof ApiError
        ? `${loadError.status}: ${loadError.message}`
        : loadError instanceof Error
          ? loadError.message
          : "Unknown error";
      setError(message);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setIsLoading(true);
    void loadCanvasState();

    const refreshHandle = window.setInterval(() => {
      void loadCanvasState();
    }, 5_000);

    return () => {
      window.clearInterval(refreshHandle);
    };
  }, [webinarId]);

  const stagingBoard = data?.boards.find((board) => board.audience === "presenter") ?? null;
  const sharedBoard = data?.boards.find((board) => board.audience === "shared") ?? null;

  async function createBoard(title: string, isPresenterOnly: boolean) {
    return apiFetch<{ data: CanvasBoardState }>(`/v1/webinars/${webinarId}/canvas/boards`, {
      method: "POST",
      body: JSON.stringify({
        title,
        isPresenterOnly,
        width: 1920,
        height: 1080,
        backgroundColor: "#f8fafc",
      }),
    });
  }

  async function initializeBoards() {
    setIsSubmitting(true);
    setFocusNotice(null);
    try {
      await createBoard("Private staging canvas", true);
      await createBoard("Shared viewer canvas", false);
      await loadCanvasState();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not initialize webinar canvases.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function addElement() {
    const targetBoard = stagingBoard ?? sharedBoard;
    if (!targetBoard) {
      return;
    }

    setIsSubmitting(true);
    setFocusNotice(null);
    try {
      await apiFetch(`/v1/webinars/${webinarId}/canvas/boards/${targetBoard.id}/elements`, {
        method: "POST",
        body: JSON.stringify(buildElementPayload(activeTool, textDraft)),
      });
      await loadCanvasState();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not add canvas artifact.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function moveElementToShared(elementId: string) {
    if (!sharedBoard) {
      return;
    }

    setIsSubmitting(true);
    try {
      await apiFetch(`/v1/webinars/${webinarId}/canvas/elements/${elementId}/move`, {
        method: "POST",
        body: JSON.stringify({ targetBoardId: sharedBoard.id }),
      });
      await loadCanvasState();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not publish the canvas artifact.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function hideElement(elementId: string) {
    setIsSubmitting(true);
    try {
      await apiFetch(`/v1/webinars/${webinarId}/canvas/elements/${elementId}/hide`, {
        method: "PATCH",
      });
      await loadCanvasState();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not hide the canvas artifact.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function addDefaultFogZone() {
    if (!sharedBoard) {
      return;
    }

    const nextZone = {
      id: crypto.randomUUID(),
      x: 180,
      y: 140,
      width: 560,
      height: 220,
      label: "Reveal window",
    };

    setIsSubmitting(true);
    try {
      await apiFetch(`/v1/webinars/${webinarId}/canvas/boards/${sharedBoard.id}/fog`, {
        method: "PUT",
        body: JSON.stringify({
          zones: [...sharedBoard.fogZones.map((zone) => ({
            id: zone.id,
            x: zone.x,
            y: zone.y,
            width: zone.width,
            height: zone.height,
            label: zone.label,
          })), nextZone],
        }),
      });
      await loadCanvasState();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not add a fog zone.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function toggleFogZone(zoneId: string, reveal: boolean) {
    if (!sharedBoard) {
      return;
    }

    setIsSubmitting(true);
    try {
      await apiFetch(`/v1/webinars/${webinarId}/canvas/boards/${sharedBoard.id}/fog/reveal`, {
        method: "POST",
        body: JSON.stringify({ zoneId, reveal }),
      });
      await loadCanvasState();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not update the reveal state.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function sendFocusSignal(kind: "focus" | "clear") {
    if (!sharedBoard) {
      return;
    }

    setIsSubmitting(true);
    setFocusNotice(null);
    try {
      if (kind === "focus") {
        await apiFetch(`/v1/webinars/${webinarId}/canvas/boards/${sharedBoard.id}/focus`, {
          method: "POST",
          body: JSON.stringify({ x: 420, y: 260, zoom: 1.2 }),
        });
        setFocusNotice("Focus route fired. Attendee viewport handoff still depends on realtime transport, so browser proof stays scoped to the route contract in this pass.");
      } else {
        await apiFetch(`/v1/webinars/${webinarId}/canvas/boards/${sharedBoard.id}/focus/clear`, {
          method: "POST",
        });
        setFocusNotice("Return-to-presenter route fired. Shared viewport reset remains transport-scoped until the realtime lane consumes these focus envelopes.");
      }
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not send the focus control signal.");
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return <CanvasRendererSkeleton variant={variant} />;
  }

  if (variant === "overlay") {
    if (!sharedBoard && !error) {
      return null;
    }

    return (
      <div className="pointer-events-none absolute inset-0 z-20" data-testid={`canvas-renderer-${mode}`}>
        {sharedBoard ? <BoardStage board={sharedBoard} variant="overlay" mode={mode} /> : null}
        {error ? (
          <div className="absolute left-4 top-4 rounded-md bg-black/75 px-3 py-2 text-xs text-rose-200">
            {error}
          </div>
        ) : null}
      </div>
    );
  }

  const timeline = [...(data?.timeline ?? [])].reverse().slice(0, 6);

  return (
    <div className="flex h-full flex-col rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)]" data-testid={`canvas-renderer-${mode}`}>
      {mode === "presenter" ? (
        <div className="border-b border-[var(--color-border)] px-4 py-4">
          <div className="flex flex-wrap items-center gap-2">
            {TOOL_CONFIG.map((tool) => (
              <button
                key={tool.key}
                type="button"
                onClick={() => setActiveTool(tool.key)}
                className={`rounded-full px-3 py-2 text-xs font-medium transition ${
                  activeTool === tool.key
                    ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
                    : "bg-[var(--color-surface)] text-[var(--color-muted-foreground)]"
                }`}
              >
                {tool.label}
              </button>
            ))}
          </div>
          <div className="mt-3 flex flex-col gap-3 xl:flex-row xl:items-center">
            <input
              type="text"
              value={textDraft}
              onChange={(event) => setTextDraft(event.target.value)}
              className="min-w-[220px] flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              placeholder="Artifact prompt or text"
            />
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void addElement()}
                disabled={isSubmitting || (!stagingBoard && !sharedBoard)}
                className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] disabled:opacity-60"
              >
                Add to staging
              </button>
              <button
                type="button"
                onClick={() => void addDefaultFogZone()}
                disabled={isSubmitting || !sharedBoard}
                className="rounded-md border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-muted-foreground)] disabled:opacity-60"
              >
                Add fog zone
              </button>
              <button
                type="button"
                onClick={() => void sendFocusSignal("focus")}
                disabled={isSubmitting || !sharedBoard}
                className="rounded-md border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-muted-foreground)] disabled:opacity-60"
              >
                Focus attendees
              </button>
              <button
                type="button"
                onClick={() => void sendFocusSignal("clear")}
                disabled={isSubmitting || !sharedBoard}
                className="rounded-md border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-muted-foreground)] disabled:opacity-60"
              >
                Return to presenter
              </button>
            </div>
          </div>
          <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
            Persisted canvas state now drives two boards and artifact authorship. Drawing zones, grouping gestures, and live attendee viewport transport stay explicitly scoped in the gap map until the realtime lane consumes the richer canvas events.
          </p>
          {focusNotice ? (
            <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              {focusNotice}
            </div>
          ) : null}
        </div>
      ) : null}

      {!stagingBoard && !sharedBoard && mode === "presenter" ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
          <Shield className="h-8 w-8 text-[var(--color-primary)]" />
          <div>
            <h3 className="text-base font-semibold text-[var(--color-foreground)]">Initialize the two-canvas webinar layout</h3>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
              Presenters get a private staging board and attendees get a separate shared board.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void initializeBoards()}
            disabled={isSubmitting}
            className="rounded-full bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] disabled:opacity-60"
          >
            Create staging and shared canvases
          </button>
        </div>
      ) : (
        <div className="grid flex-1 gap-4 overflow-hidden p-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(18rem,0.6fr)]">
          <div className={`grid min-h-0 gap-4 ${mode === "presenter" ? "xl:grid-cols-2" : "grid-cols-1"}`}>
            {mode === "presenter" && stagingBoard ? (
              <BoardPanel
                board={stagingBoard}
                mode={mode}
                onMoveToShared={sharedBoard ? moveElementToShared : undefined}
                onHideElement={hideElement}
                onToggleFogZone={toggleFogZone}
              />
            ) : null}
            {sharedBoard ? (
              <BoardPanel
                board={sharedBoard}
                mode={mode}
                onMoveToShared={undefined}
                onHideElement={mode === "presenter" ? hideElement : undefined}
                onToggleFogZone={toggleFogZone}
              />
            ) : null}
          </div>

          <aside className="min-h-0 overflow-y-auto rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Artifact timeline</h3>
              <span className="text-xs text-[var(--color-muted-foreground)]">
                {data?.summary.totalArtifacts ?? 0} saved
              </span>
            </div>
            <div className="mt-4 space-y-3">
              {timeline.length === 0 ? (
                <p className="text-sm text-[var(--color-muted-foreground)]">No persisted canvas artifacts yet.</p>
              ) : (
                timeline.map((entry) => (
                  <div key={entry.id} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
                        {entry.boardAudience === "presenter" ? "Staging" : "Shared"}
                      </span>
                      <span className="text-[10px] text-[var(--color-muted-foreground)]">
                        {entry.timestampSeconds == null ? "Live" : formatReplayTimestamp(entry.timestampSeconds)}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-[var(--color-foreground)]">{entry.summary}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] text-[var(--color-muted-foreground)]">
                      <span>{entry.boardTitle}</span>
                      <span>•</span>
                      <span>{entry.createdBy}</span>
                      <span>•</span>
                      <span>{entry.interactionCount} interactions</span>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="mt-6 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
              <h4 className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
                Participation proof
              </h4>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <StatChip icon={Layers} label="Total artifacts" value={String(data?.summary.totalArtifacts ?? 0)} />
                <StatChip icon={Users} label="Attendee artifacts" value={String(data?.summary.attendeeArtifacts ?? 0)} />
                <StatChip icon={Sparkles} label="Presenter artifacts" value={String(data?.summary.presenterArtifacts ?? 0)} />
              </div>
            </div>

            {error ? (
              <div className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                {error}
              </div>
            ) : null}
          </aside>
        </div>
      )}
    </div>
  );
}

function BoardPanel(
  {
    board,
    mode,
    onMoveToShared,
    onHideElement,
    onToggleFogZone,
  }: {
    board: CanvasBoardState;
    mode: "presenter" | "viewer";
    onMoveToShared?: (elementId: string) => Promise<void>;
    onHideElement?: (elementId: string) => Promise<void>;
    onToggleFogZone?: (zoneId: string, reveal: boolean) => Promise<void>;
  },
) {
  return (
    <section className="flex min-h-0 flex-col rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)]">
      <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
        <div>
          <div className="flex items-center gap-2">
            {board.audience === "presenter" ? (
              <Shield className="h-4 w-4 text-amber-500" />
            ) : (
              <Users className="h-4 w-4 text-emerald-500" />
            )}
            <h3 className="text-sm font-semibold text-[var(--color-foreground)]">{board.title}</h3>
          </div>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {board.audience === "presenter"
              ? "Presenter-only staging board"
              : "Shared viewer board visible to attendees"}
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
          {board.elements.length}/{board.maxElements}
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <BoardStage board={board} variant="panel" mode={mode} />

        {board.fogZones.length > 0 ? (
          <div className="mt-4 space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
              Fog zones
            </h4>
            {board.fogZones.map((zone) => (
              <div key={zone.id} className="flex items-center justify-between rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-xs">
                <span className="text-[var(--color-foreground)]">{zone.label ?? "Reveal zone"}</span>
                {mode === "presenter" && onToggleFogZone ? (
                  <button
                    type="button"
                    onClick={() => void onToggleFogZone(zone.id, !zone.isRevealed)}
                    className="rounded-full border border-[var(--color-border)] px-2 py-1 text-[10px] text-[var(--color-muted-foreground)]"
                  >
                    {zone.isRevealed ? "Hide again" : "Reveal"}
                  </button>
                ) : (
                  <span className="text-[var(--color-muted-foreground)]">{zone.isRevealed ? "Visible" : "Hidden"}</span>
                )}
              </div>
            ))}
          </div>
        ) : null}

        <div className="mt-4 space-y-2">
          {board.elements.length === 0 ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">No artifacts on this board yet.</p>
          ) : (
            board.elements.map((element) => (
              <div key={element.id} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-[var(--color-foreground)]">{element.summary}</p>
                    <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">
                      {element.createdBy} • {element.interactionCount} interactions
                    </p>
                  </div>
                  {mode === "presenter" ? (
                    <div className="flex items-center gap-2">
                      {board.audience === "presenter" && onMoveToShared ? (
                        <button
                          type="button"
                          onClick={() => void onMoveToShared(element.id)}
                          className="rounded-full border border-[var(--color-border)] px-2 py-1 text-[10px] text-[var(--color-muted-foreground)]"
                        >
                          <ArrowRightLeft className="mr-1 inline h-3 w-3" />
                          Publish
                        </button>
                      ) : null}
                      {onHideElement ? (
                        <button
                          type="button"
                          onClick={() => void onHideElement(element.id)}
                          className="rounded-full border border-[var(--color-border)] px-2 py-1 text-[10px] text-[var(--color-muted-foreground)]"
                        >
                          Hide
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}

function BoardStage(
  {
    board,
    variant,
    mode,
  }: {
    board: CanvasBoardState;
    variant: "panel" | "overlay";
    mode: "presenter" | "viewer";
  },
) {
  const shellClass = variant === "overlay"
    ? "absolute inset-0"
    : "relative aspect-video overflow-hidden rounded-2xl border border-[var(--color-border)]";

  return (
    <div className={shellClass} style={variant === "panel" ? { backgroundColor: board.backgroundColor } : undefined}>
      {variant === "overlay" ? (
        <div className="absolute left-4 top-4 rounded-full bg-black/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white">
          Shared canvas
        </div>
      ) : null}
      {board.elements.map((element) => (
        <CanvasElementView key={element.id} board={board} element={element} variant={variant} />
      ))}
      {board.fogZones.map((zone) => (
        <FogZoneView key={zone.id} board={board} zone={zone} variant={variant} mode={mode} />
      ))}
    </div>
  );
}

function CanvasElementView(
  {
    board,
    element,
    variant,
  }: {
    board: CanvasBoardState;
    element: CanvasBoardElement;
    variant: "panel" | "overlay";
  },
) {
  const left = (element.x / board.width) * 100;
  const top = (element.y / board.height) * 100;
  const width = Math.max((element.width / board.width) * 100, 6);
  const height = Math.max((element.height / board.height) * 100, 8);
  const baseStyle = {
    left: `${left}%`,
    top: `${top}%`,
    width: `${width}%`,
    height: `${height}%`,
    transform: `rotate(${element.rotation}deg)`,
  };
  const content = element.content ?? {};

  if (element.elementType === "sticky_note") {
    return (
      <div
        className="absolute rounded-xl border border-amber-300/40 bg-amber-100/95 p-3 text-xs text-amber-950 shadow-lg"
        style={baseStyle}
      >
        <p className="line-clamp-4">{typeof content.text === "string" ? content.text : element.summary}</p>
        {variant === "panel" ? (
          <div className="mt-2 text-[10px] text-amber-800/80">{element.createdBy}</div>
        ) : null}
      </div>
    );
  }

  if (element.elementType === "text_block") {
    return (
      <div
        className="absolute rounded-xl bg-black/70 px-3 py-2 text-xs font-medium text-white shadow-lg backdrop-blur"
        style={baseStyle}
      >
        {typeof content.text === "string" ? content.text : element.summary}
      </div>
    );
  }

  if (element.elementType === "poll_card") {
    const options = Array.isArray(content.options) ? content.options : [];
    return (
      <div className="absolute rounded-2xl border border-sky-300/30 bg-slate-950/85 p-3 text-white shadow-lg" style={baseStyle}>
        <p className="text-xs font-semibold">{typeof content.question === "string" ? content.question : "Live poll"}</p>
        <div className="mt-2 space-y-1">
          {options.slice(0, 3).map((option: any) => (
            <div key={option.id ?? option.label} className="rounded-full bg-white/10 px-2 py-1 text-[10px]">
              {option.label ?? option.text ?? option.id}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (element.elementType === "word_cloud") {
    const words = Array.isArray(content.words) ? content.words : [];
    return (
      <div className="absolute rounded-2xl border border-violet-300/30 bg-violet-950/80 p-3 text-white shadow-lg" style={baseStyle}>
        <p className="text-xs font-semibold">{typeof content.prompt === "string" ? content.prompt : "Word cloud"}</p>
        <div className="mt-2 flex flex-wrap gap-1">
          {words.length > 0 ? words.slice(0, 5).map((word: any) => (
            <span key={word.text} className="rounded-full bg-white/10 px-2 py-0.5 text-[10px]">
              {word.text} {word.count ? `(${word.count})` : ""}
            </span>
          )) : (
            <span className="text-[10px] text-white/70">Waiting for audience words</span>
          )}
        </div>
      </div>
    );
  }

  if (element.elementType === "emoji_zone") {
    return (
      <div className="absolute rounded-2xl border border-emerald-300/30 bg-emerald-950/60 p-3 text-white shadow-lg" style={baseStyle}>
        <div className="flex items-center gap-2 text-xs font-semibold">
          <PenLine className="h-3.5 w-3.5" />
          Emoji / reaction zone
        </div>
        <p className="mt-2 text-[10px] text-white/70">Interactive area for lightweight audience input.</p>
      </div>
    );
  }

  return (
    <div className="absolute rounded-2xl border border-[var(--color-border)] bg-black/60 p-3 text-xs text-white shadow-lg" style={baseStyle}>
      {element.summary}
    </div>
  );
}

function FogZoneView(
  {
    board,
    zone,
    variant,
    mode,
  }: {
    board: CanvasBoardState;
    zone: CanvasFogZone;
    variant: "panel" | "overlay";
    mode: "presenter" | "viewer";
  },
) {
  const left = (zone.x / board.width) * 100;
  const top = (zone.y / board.height) * 100;
  const width = (zone.width / board.width) * 100;
  const height = (zone.height / board.height) * 100;

  if (zone.isRevealed) {
    return (
      <div
        className="absolute rounded-2xl border-2 border-dashed border-cyan-300/90"
        style={{ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%` }}
      >
        {variant === "panel" ? (
          <div className="absolute left-2 top-2 rounded-full bg-cyan-500/85 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-cyan-950">
            {zone.label ?? "Revealed"}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`absolute rounded-2xl ${mode === "presenter" && variant === "panel" ? "border-2 border-dashed border-slate-900/40 bg-slate-950/25" : "bg-slate-950/88"}`}
      style={{ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%` }}
    >
      {mode === "presenter" && variant === "panel" ? (
        <div className="absolute left-2 top-2 rounded-full bg-slate-950/80 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-100">
          Hidden from attendees
        </div>
      ) : null}
    </div>
  );
}

function StatChip({ icon: Icon, label, value }: { icon: typeof Layers; label: string; value: string }) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-3">
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-[var(--color-muted-foreground)]" />
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">{label}</span>
      </div>
      <div className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">{value}</div>
    </div>
  );
}

function buildElementPayload(tool: CanvasTool, textDraft: string) {
  if (tool === "text_block") {
    return {
      elementType: "text_block",
      x: 120,
      y: 120,
      width: 340,
      height: 120,
      content: {
        text: textDraft,
        fontSize: 32,
        fontWeight: "bold",
        textAlign: "left",
      },
    };
  }

  if (tool === "poll_card") {
    return {
      elementType: "poll_card",
      x: 140,
      y: 160,
      width: 360,
      height: 180,
      content: {
        question: textDraft,
        options: [
          { id: "yes", label: "Yes", voteCount: 0 },
          { id: "no", label: "No", voteCount: 0 },
        ],
        isOpen: true,
      },
    };
  }

  if (tool === "word_cloud") {
    return {
      elementType: "word_cloud",
      x: 180,
      y: 180,
      width: 380,
      height: 200,
      content: {
        prompt: textDraft,
        words: [],
      },
    };
  }

  if (tool === "emoji_zone") {
    return {
      elementType: "emoji_zone",
      x: 260,
      y: 200,
      width: 320,
      height: 220,
      content: {
        allowedEmojis: ["🔥", "👏", "💡", "✅"],
        placements: [],
      },
    };
  }

  return {
    elementType: "sticky_note",
    x: 100,
    y: 120,
    width: 220,
    height: 160,
    content: {
      text: textDraft,
      color: "#fef08a",
    },
  };
}

function formatReplayTimestamp(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
}

function CanvasRendererSkeleton({ variant }: { variant: "panel" | "overlay" }) {
  if (variant === "overlay") {
    return (
      <div className="pointer-events-none absolute inset-0 z-20">
        <Skeleton className="absolute left-6 top-6 h-12 w-48 rounded-xl bg-white/10" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)]">
      <Skeleton className="h-14 rounded-none" />
      <div className="flex flex-1 items-center justify-center">
        <SkeletonCard className="h-48 w-64" />
      </div>
    </div>
  );
}
